import { Highlightable } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import {
  OptionLike,
  OptionSelectionChange,
  SELECT_PARENT,
  SelectParent,
} from './select.types';

let nextOptionId = 0;

/**
 * Single selectable item inside an `<app-select>` panel. Mirrors `<mat-option>`
 * behaviour: click / Enter / Space toggles selection, parent decides whether to
 * close the panel or keep it open (multiple). When the parent is in `multiple`
 * mode this option renders a pseudo-checkbox; when single, a checkmark icon is
 * shown after the label only when selected (unless `hideSingleSelectionIndicator`).
 */
@Component({
  selector: 'app-option',
  templateUrl: './option.component.html',
  styleUrls: ['./option.component.scss'],
  exportAs: 'appOption',
  host: {
    '[class]': 'classes',
    role: 'option',
    '[id]': 'id',
    '[attr.tabindex]': '_getTabIndex()',
    '[attr.aria-selected]': 'selected',
    '[attr.aria-disabled]': 'disabled.toString()',
    '(click)': '_handleClick()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionComponent implements OptionLike, Highlightable {
  private readonly _uniqueId = `app-option-${nextOptionId++}`;
  private _selected = false;
  private _active = false;
  private _disabled = false;
  private _id?: string;

  @Input() value: any;

  @Input()
  get id(): string {
    return this._id ?? this._uniqueId;
  }
  set id(value: string) {
    this._id = value;
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }

  @Output() readonly onSelectionChange =
    new EventEmitter<OptionSelectionChange>();

  @ViewChild('text', { static: true }) _text?: ElementRef<HTMLElement>;

  get selected(): boolean {
    return this._selected;
  }

  get active(): boolean {
    return this._active;
  }

  get multiple(): boolean {
    return !!this.parent?.multiple;
  }

  get hideSingleSelectionIndicator(): boolean {
    return !!this.parent?.hideSingleSelectionIndicator;
  }

  get viewValue(): string {
    const ref = this._text?.nativeElement ?? this._element.nativeElement;
    return (ref.textContent || '').trim();
  }

  get classes(): string {
    const classes = ['app-option'];
    if (this._selected) classes.push('app-option--selected');
    if (this._active) classes.push('app-option--active');
    if (this._disabled) classes.push('app-option--disabled');
    if (this.multiple) classes.push('app-option--multiple');
    return classes.join(' ');
  }

  constructor(
    private _element: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef,
    @Optional() @Inject(SELECT_PARENT) public parent: SelectParent | null
  ) {}

  /** Programmatic selection — does NOT report user input. */
  select(): void {
    if (!this._selected) {
      this._selected = true;
      this._cdr.markForCheck();
      this._emitSelection(false);
    }
  }

  /** Programmatic deselection — does NOT report user input. */
  deselect(): void {
    if (this._selected) {
      this._selected = false;
      this._cdr.markForCheck();
      this._emitSelection(false);
    }
  }

  setActiveStyles(): void {
    if (!this._active) {
      this._active = true;
      this._cdr.markForCheck();
    }
  }

  setInactiveStyles(): void {
    if (this._active) {
      this._active = false;
      this._cdr.markForCheck();
    }
  }

  getLabel(): string {
    return this.viewValue;
  }

  focus(): void {
    this._element.nativeElement.focus();
  }

  /** Required by `Highlightable` for ActiveDescendantKeyManager typeahead. */
  getElementRef(): ElementRef<HTMLElement> {
    return this._element;
  }

  /** Toggle selection from a user gesture and notify the parent. */
  _selectViaInteraction(): void {
    if (this._disabled) {
      return;
    }
    if (this.multiple) {
      this._selected = !this._selected;
    } else {
      this._selected = true;
    }
    this._cdr.markForCheck();
    this._emitSelection(true);
  }

  _handleClick(): void {
    this._selectViaInteraction();
  }

  _markForCheck(): void {
    this._cdr.markForCheck();
  }

  // Options never participate in the natural tab order — they are managed by
  // the parent's `ActiveDescendantKeyManager`. Disabled state is communicated
  // via `aria-disabled`, not tabindex.
  _getTabIndex(): string {
    return '-1';
  }

  /** Internal — used by the parent to set selected state without re-emitting. */
  _setSelected(selected: boolean): void {
    if (this._selected !== selected) {
      this._selected = selected;
      this._cdr.markForCheck();
    }
  }

  private _emitSelection(isUserInput: boolean): void {
    this.onSelectionChange.emit({ source: this, isUserInput });
  }
}
